import {isNotNil} from 'ramda';

import {RecordIdHttpModel} from '@dms/api/shared';
import {SnippetProps} from '@dms/shared';

import {Nullish} from 'shared/web';

import {useRecordAuditBranchSnippet} from '../branchSnippet/useRecordAuditBranchSnippet';
import {usePairedRecordSnippet} from '../pairedRecord/usePairedRecordSnippet';

interface UseRecordAuditHeaderSnippetsArgs {
  /** Prefix for the branch snippet test id, e.g. `recordAuditDetail` -> `recordAuditDetail-header-branchSnippet`. */
  testIdPrefix: string;
  /** Paired record (or null) rendered as the main + vehicle snippets. */
  record: RecordIdHttpModel | null;
  /** Whether the paired record exposes its edit/unpair affordance (detail: audit OPEN; create: false). */
  isPairEditable: boolean;
  onPair: () => void;
  /** Detail only — skeletons the record snippet while a just-picked/cleared record is persisting. */
  isRecordUpdating?: boolean;
  branchId: string;
  /** Authoritative branch name when available (detail has it from the audit snapshot). */
  branchName?: string | null;
  isBranchEditable: boolean;
  /** Detail only — skeletons the branch name while a just-picked branch is persisting. */
  isBranchUpdating?: boolean;
  onSelectBranch: (branchId: string) => void;
}

interface UseRecordAuditHeaderSnippetsResult {
  /** Ready-to-render `EntityHeader` snippet array: paired record, optional vehicle, branch. */
  snippets: SnippetProps[];
  /** Id of the linked vehicle; drives the header photo + gallery lightbox via useVehicleHeaderImage. */
  vehicleId: string | null;
  /** Plain-string entity number of the paired record (create wizard step subtitle); never the UUID. */
  recordTitle: string | Nullish;
}

/**
 * Assembles the record-audit entity-header snippets (paired record + optional vehicle + branch)
 * shared verbatim by the create wizard header and the audit detail header. The detail page passes
 * the optimistic `isRecordUpdating` / `isBranchUpdating` flags; the create page omits them.
 */
export function useRecordAuditHeaderSnippets(
  args: UseRecordAuditHeaderSnippetsArgs
): UseRecordAuditHeaderSnippetsResult {
  const {mainSnippet, vehicleSnippet, vehicleId, recordTitle} = usePairedRecordSnippet({
    record: args.record,
    isEditable: args.isPairEditable,
    isUpdating: args.isRecordUpdating,
    onPair: args.onPair,
  });

  const branchSnippet = useRecordAuditBranchSnippet({
    branchId: args.branchId,
    branchName: args.branchName,
    isEditable: args.isBranchEditable,
    isLoading: args.isBranchUpdating,
    onSelect: args.onSelectBranch,
    'data-testid': `${args.testIdPrefix}-header-branchSnippet`,
  });

  const snippets: SnippetProps[] = [
    mainSnippet,
    ...(isNotNil(vehicleSnippet) ? [vehicleSnippet] : []),
    branchSnippet,
  ];

  return {snippets, vehicleId, recordTitle};
}
